/**
 * CLI authorization / consent page.
 *
 * The `plb` CLI opens the browser here with a loopback redirect_uri, a PKCE
 * code_challenge, and a state. The user is already authenticated (Frontegg
 * embedded login, enforced by AuthenticatedRootPage).
 *
 * UX (mirrors the sidebar TenantSwitcher): the current account is shown as a
 * fact. Changing it is an explicit modal that performs the tenant switch FIRST;
 * authorizing then grants a session for whatever account is current. This keeps
 * "which client" and "authorize" as two distinct, deliberate steps.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppAuth, useAppAuthActions, useAppTenantsState } from '@/hooks/useAppAuth';
import ApiService from '@/services/api.service';
import { Button } from '@/components/ui/button';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeftRight } from 'lucide-react';

function isLoopback(uri: string): boolean {
  try {
    const u = new URL(uri);
    return u.protocol === 'http:' && (u.hostname === '127.0.0.1' || u.hostname === 'localhost');
  } catch {
    return false;
  }
}

export default function CliAuth() {
  const [params] = useSearchParams();
  const redirectUri = params.get('redirect_uri') || '';
  const state = params.get('state') || '';
  const codeChallenge = params.get('code_challenge') || '';

  const { user } = useAppAuth();
  const { switchTenant } = useAppAuthActions();
  const { tenants } = useAppTenantsState();

  const [changeOpen, setChangeOpen] = useState(false);
  const [pendingTenantId, setPendingTenantId] = useState<string | undefined>(undefined);
  const [switching, setSwitching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options: ComboboxOption[] = useMemo(
    () => (tenants || []).map((t) => ({ value: t.tenantId, label: t.name })),
    [tenants]
  );
  const currentTenantId = user?.tenantId;
  const currentName =
    options.find((o) => o.value === currentTenantId)?.label || currentTenantId || 'this account';
  const canSwitch = (tenants?.length || 0) >= 2;

  // Guard rails — never redirect a code anywhere but the CLI's loopback.
  if (!redirectUri || !codeChallenge) {
    return <Centered>Invalid CLI auth request (missing parameters).</Centered>;
  }
  if (!isLoopback(redirectUri)) {
    return <Centered>Refusing to authorize: redirect target is not a local loopback address.</Centered>;
  }

  const openChange = () => {
    setPendingTenantId(currentTenantId);
    setError(null);
    setChangeOpen(true);
  };

  const applySwitch = () => {
    if (!pendingTenantId || pendingTenantId === currentTenantId) {
      setChangeOpen(false);
      return;
    }
    setSwitching(true);
    // Switch the active tenant in place (we're on the main app domain during
    // CLI auth, so no custom-domain redirect — that would drop our query
    // params). On success, useAppAuth re-renders with the new tenantId.
    switchTenant({
      tenantId: pendingTenantId,
      callback: (success: boolean) => {
        setSwitching(false);
        if (success) {
          setChangeOpen(false);
        } else {
          setError('Failed to switch account.');
        }
      },
    } as any);
  };

  const authorize = async () => {
    setBusy(true);
    setError(null);
    try {
      const resp = (await ApiService.getInstance().request('POST', '/cli/issue', {
        code_challenge: codeChallenge,
        state,
      })) as { code: string };
      const sep = redirectUri.includes('?') ? '&' : '?';
      window.location.href =
        `${redirectUri}${sep}code=${encodeURIComponent(resp.code)}&state=${encodeURIComponent(state)}`;
    } catch (e: any) {
      setBusy(false);
      setError(e?.message || 'Failed to authorize the CLI.');
    }
  };

  return (
    <Centered>
      <div className="w-full max-w-md space-y-5 rounded-lg border bg-card p-6 shadow-sm">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold">Authorize CLI access</h1>
          <p className="text-sm text-muted-foreground">
            The <span className="font-medium">plb</span> command-line tool is requesting access to
            act on behalf of a client account. You'll authorize as the account below — change it
            first if you need a different client.
          </p>
        </div>

        <div className="space-y-1.5">
          <span className="text-sm font-medium">Account</span>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <span className="truncate text-sm font-medium">{currentName}</span>
            {canSwitch && (
              <Button variant="ghost" size="sm" className="ml-2 shrink-0 gap-1.5" onClick={openChange}>
                <ArrowLeftRight className="h-3.5 w-3.5" />
                Change
              </Button>
            )}
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => window.close()} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={authorize} disabled={busy || !user}>
            {busy ? 'Authorizing…' : `Authorize for ${currentName}`}
          </Button>
        </div>
      </div>

      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change account</DialogTitle>
            <DialogDescription>
              Switch the active account before authorizing the CLI.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Combobox
              options={options}
              value={pendingTenantId}
              onValueChange={setPendingTenantId}
              placeholder="Select account..."
              emptyText="No accounts found."
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeOpen(false)} disabled={switching}>
              Cancel
            </Button>
            <Button
              onClick={applySwitch}
              disabled={switching || !pendingTenantId || pendingTenantId === currentTenantId}
            >
              {switching ? 'Switching…' : 'Change account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6 text-center">{children}</div>
  );
}
